import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import { useCallback, useContext, useEffect, useState } from "react";

import type { BillingCustomerAssociation } from "~/shared/schemas/billing-customer-schema.ts";

import { QuoteDetailsContext } from "../../../../context/quote-details-context.tsx";
import { useBillingCustomers } from "../../../../hooks/use-billing-customers.ts";
import styles from "./billing-customer-dropdown.module.css";

export const BillingCustomerDropdown = ({
	className,
	customerId: customerIdProp,
	billingCustomerId: billingCustomerIdProp,
	onChange,
}: {
	readonly className?: string;
	/**
	 * When `onChange` is provided the component is "controlled": the customer id,
	 * selected billing customer, and change handler come from props (used by the
	 * shipment-management customer card, which has no QuoteDetailsContext).
	 * Otherwise it reads/writes QuoteDetailsContext as before (quote flow).
	 */
	readonly customerId?: number;
	readonly billingCustomerId?: number | null;
	readonly onChange?: (
		billingCustomer: BillingCustomerAssociation | null,
	) => void;
}) => {
	const { quoteState, setQuoteState } = useContext(QuoteDetailsContext);
	const isControlled = onChange != null;
	const customerId = isControlled ? customerIdProp : quoteState.customer?.id;
	const selectedBillingCustomerId = isControlled
		? (billingCustomerIdProp ?? null)
		: quoteState.billingCustomerId;

	const { data: billingCustomers } = useBillingCustomers(customerId);

	const [selectedBillingCustomer, setSelectedBillingCustomer] =
		useState<BillingCustomerAssociation | null>(null);

	// Set initial selection when data loads
	useEffect(() => {
		if (!billingCustomers || billingCustomers.length === 0) return;

		if (selectedBillingCustomerId != null) {
			const match = billingCustomers.find(
				(bc) => bc.customerId === selectedBillingCustomerId,
			);
			if (match) {
				setSelectedBillingCustomer(match);
				return;
			}
		}

		setSelectedBillingCustomer(null);
	}, [billingCustomers, selectedBillingCustomerId]);

	const handleChange = useCallback(
		(e: { value: BillingCustomerAssociation | null }) => {
			const selected = e.value;
			setSelectedBillingCustomer(selected);
			if (onChange) {
				onChange(selected);
				return;
			}
			setQuoteState((prev) => ({
				...prev,
				billingCustomerId: selected?.customerId ?? null,
			}));
		},
		[onChange, setQuoteState],
	);

	// Only show when there are multiple billing customers (a single association
	// is just the tendering customer itself, so there is nothing to choose).
	if (!billingCustomers || billingCustomers.length <= 1) {
		return null;
	}

	return (
		<div
			className={`${styles.billingCustomerDropdown}${
				className ? ` ${className}` : ""
			}`}
		>
			<div className={styles.billingCustomerTitle}>Billing Customer</div>
			<DropDownList
				data={billingCustomers}
				dataItemKey={"customerId"}
				defaultItem={{
					customerId: null,
					customerName: "",
					ftCustomerId: 0,
				}}
				onChange={handleChange}
				style={{ width: 350 }}
				textField={"customerName"}
				value={selectedBillingCustomer}
			/>
		</div>
	);
};
